import {useEffect, useRef} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useQueryClient} from 'react-query'
import {getToken, joinConversationInvite} from 'api'
import {PENDING_INVITE_KEY} from 'pages/JoinCircle'

// Auth/onboarding surfaces where redemption must wait: interrupting the
// register → verify → onboarding chain would strand the user mid-flow.
const DEFERRED_PREFIXES = [
  '/join',
  '/login',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/auth',
  '/onboarding',
]

/**
 * Completes a Circle join that started logged-out. The /join page stashes
 * the invite token before routing into register/login; this component
 * (mounted once in App) redeems it on the first authenticated page load
 * outside the auth flow — which is exactly the moment onboarding drops the
 * new user on /homepage — and lands them inside their Circle.
 */
const PendingInviteRedeemer = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const redeemingRef = useRef(false)

  useEffect(() => {
    const pendingToken = (() => {
      try {
        return localStorage.getItem(PENDING_INVITE_KEY)
      } catch {
        return null
      }
    })()
    if (!pendingToken || redeemingRef.current) return
    if (!getToken()?.token) return
    if (DEFERRED_PREFIXES.some((p) => location.pathname.startsWith(p))) return

    redeemingRef.current = true
    joinConversationInvite(pendingToken)
      .then((result) => {
        try {
          localStorage.removeItem(PENDING_INVITE_KEY)
        } catch {
          // ignore
        }
        if (result?.id) {
          queryClient.invalidateQueries('conversations')
          navigate(`/messages/${result.id}`)
        }
      })
      .catch(() => {
        // Invalid/expired by now — drop it so it doesn't retry forever.
        try {
          localStorage.removeItem(PENDING_INVITE_KEY)
        } catch {
          // ignore
        }
      })
      .finally(() => {
        redeemingRef.current = false
      })
  }, [location.pathname, navigate, queryClient])

  return null
}

export default PendingInviteRedeemer
